import {computed, DestroyRef, inject, Injectable, Signal, signal, WritableSignal} from "@angular/core";
import {AppDeviceRegistrationRequestData, AppDeviceRegistrationService} from "./app-device-registration.service";
import {AppDeviceType, ConfigurationModel} from "../configuration/configuration.model";
import {AppDeviceEntityModel} from "./app-device-entity-model.service";
import {EntityData} from "@fedelta-pos/data-client";
import {NotificationService} from "../notification/notification.service";
import {firstValueFrom, ReplaySubject, Subject, throttleTime} from "rxjs";
import {environment} from '../../environments/environment';
import {onEach} from '../utils/observable-utils';
import {DeferredInjectable} from '../deferred-injection.service';
import {PaymentModel} from '../payment/payment.model';

export enum ReceivedMessageType {
    BACK = "back",
    PAUSE = "pause",
    RESUME = "resume",
    APP_DEVICE_STATUS = "app-device-status",
    STRIPE_TOKEN_REFRESH = "stripe-token-refresh",
    TAP_TO_PAY_ERROR = "tap-to-pay-error",
    PAYMENT_INTENT_NOTIFICATION = "payment-intent-notification",
    PAYMENT_METHOD_NOTIFICATION = "payment-method-notification"
}

export enum SentMessageType {
    HANDSHAKE = "handshake",
    EXIT_VENUE = "exit-venue",
    RELOAD = "reload",
    APP_DEVICE_STATUS_REQUEST = "app-device-status-request",
    APP_DEVICE_UPDATE = "app-device-update",
    PAYMENT_AUTHORISATION = "payment-authorisation"
}

export enum AppDeviceErrorType {
    HANDLER_FAILURE = "handler-failure",
    CHEWZIE_ERROR = "chewzie-error",
    PROVIDER_ERROR = "provider-error",
    PAYMENT_CANCELLED = "payment-cancelled"
}

/**
 * Messages exchanged between the App Device and the Web App
 */
export class AppDeviceMessage<T extends ReceivedMessageType | SentMessageType> {
    constructor(public messageId: string, public type: T, public data: string) {
    }
}

/**
 * Responses exchanged between the App Device and the Web App
 */
export class AppDeviceResponse {
    constructor(public messageId: string, public data: string) {
    }
}

// noinspection JSUnusedGlobalSymbols
/**
 * Error responses exchanged between the App Device and the Web App
 */
export class AppDeviceError {
    constructor(public messageId: string,
                public errorType: AppDeviceErrorType,
                public errorCode: string,
                public errorMessage: string) {
    }
}

export class AppDeviceStatusValue {
    constructor(public name: string,
                public value: string) {
    }
}

/**
 * Status information from the app device
 */
export class AppDeviceStatus {
    constructor(public paymentsReady: boolean,
                public statuses?: AppDeviceStatusValue[],
                public identifiers?: AppDeviceStatusValue[]) {
    }
}

export function isAppDeviceError(error: any): error is AppDeviceError {
    return error && (error as AppDeviceError).messageId !== undefined;
}

/**
 * Interface for message handlers
 */
export type AppDeviceMessageHandler = (type: ReceivedMessageType, data: any) => Promise<any>;

/**
 * A blank message to acknowledge receipt
 */
export class AppDeviceMessageAck {
    public status: string = "ack";
}

/**
 * Interface for the Web to App bridge object created by the app device
 */
export interface AppDeviceBridge {

    /**
     * Receives a message from the Web App
     * @param message JSON string containing an AppDeviceMessage
     */
    receiveMessage(message: string): void;

    /**
     * Receives a response from the Web App
     * @param response JSON string containing an AppDeviceResponse
     */
    receiveResponse(response: string): void;

    /**
     * Receives an error from the Web App
     * @param error JSON string containing an AppDeviceError
     */
    receiveError(error: string): void;
}

export class HandshakeMessage {
    public message: string = "Handshake from Web App";
}

export class HandshakeResponse {
    status: string;
    platform: string;
    version: string;
}

/**
 * Service for two-way communication between and App Device (native app) and Web App
 */
@Injectable({providedIn: "root"})
export class AppDeviceService implements DeferredInjectable {
    private destroyRef: DestroyRef = inject(DestroyRef);
    private appDeviceRegistrationService: AppDeviceRegistrationService = inject(AppDeviceRegistrationService);
    private configurationModel: ConfigurationModel = inject(ConfigurationModel);
    private appDeviceEntityModel: AppDeviceEntityModel = inject(AppDeviceEntityModel);
    private notificationService: NotificationService;
    private paymentModel: PaymentModel;

    private static readonly APP_TO_WEB_BRIDGE_NAME: string = "ChewzieAppToWebBridge";
    private static readonly WEB_TO_APP_BRIDGE_NAME: string = "ChewzieWebToAppBridge";
    private static readonly APP_DEVICE_REFRESH_TIMEOUT_MS: number = 10000;

    private readonly appDeviceBridge: WritableSignal<AppDeviceBridge> = signal<AppDeviceBridge>(undefined);
    private readonly deviceId: WritableSignal<string> = signal<string>(undefined);
    private readonly deviceType: WritableSignal<AppDeviceType> = signal<AppDeviceType>(undefined);
    private readonly _platformVersion: WritableSignal<string> = signal<string>(undefined);
    public readonly platformVersion: Signal<string> = this._platformVersion.asReadonly();
    private readonly _appDeviceStatus: WritableSignal<AppDeviceStatus> = signal<AppDeviceStatus>(undefined);
    public readonly appDeviceStatus: Signal<AppDeviceStatus> = this._appDeviceStatus.asReadonly();

    public readonly isDevice: Signal<boolean> = computed<boolean>(() => !!this.deviceId());
    public readonly isAvailable: Signal<boolean> = computed<boolean>(() => !!this.appDeviceBridge());
    // The name of the device as configured in back office
    public readonly deviceName: Signal<string> = computed(() => this.isAvailable() ? this.appDeviceEntityModel.dataSignal()?.name : undefined);
    // The chewzie location id of the device as configured in the back office
    public readonly deviceLocationId: Signal<number> = computed<number>(() => this.isAvailable() ? this.appDeviceEntityModel.dataSignal()?.location.locationId : undefined);
    // Indicates that this device requires registration in back office
    public readonly deviceRegistrationRequired: Signal<boolean> = computed<boolean>(() => this.isAvailable() && (!this.appDeviceEntityModel.dataSignal()?.registered || this.appDeviceEntityModel.dataSignal()?.discontinued) && !!this.appDeviceEntityModel.dataSignal()?.name);

    private appDeviceRefreshRequest$: Subject<AppDeviceRegistrationRequestData> = new Subject<AppDeviceRegistrationRequestData>();
    private appDeviceRefreshResponse$: Subject<any> = new ReplaySubject(1, AppDeviceService.APP_DEVICE_REFRESH_TIMEOUT_MS);

    private messageHandlers: Map<ReceivedMessageType, AppDeviceMessageHandler> = new Map();
    private pendingResponses: Map<string, Subject<string>> = new Map();
    private messageCounter: number = 0;
    private deviceRegistrationSent: boolean = false;

    constructor() {
        console.log("--- AppDeviceService");

        this.appDeviceBridge.set(window[AppDeviceService.WEB_TO_APP_BRIDGE_NAME]);
        if (!this.isAvailable()) {
            console.log("App Device not available");
            return;
        }

        this.registerMessageHandler(ReceivedMessageType.APP_DEVICE_STATUS, async (type: ReceivedMessageType, appDeviceStatus: AppDeviceStatus) => this._appDeviceStatus.set(appDeviceStatus));

        this.registerMessageHandler(ReceivedMessageType.PAUSE, async () => {
            console.log("App Device paused");
            return new AppDeviceMessageAck();
        });

        this.registerMessageHandler(ReceivedMessageType.RESUME, async () => {
            console.log("App Device Resumed");
            document.documentElement.style.display = "none";
            setTimeout(() => document.documentElement.style.display = "block", 500);

            // Ensure the App Device has the latest config
            await this.refreshAppDevice("app device resumed");
            await this.sendMessage(SentMessageType.APP_DEVICE_STATUS_REQUEST, {});

            return new AppDeviceMessageAck();
        });

        // Assign the bridge object to the window object
        (window as any)[AppDeviceService.APP_TO_WEB_BRIDGE_NAME] = {

            /**
             * Receives a message from the App Device
             * @param message The message from the app device
             */
            receiveMessage: async (message: AppDeviceMessage<ReceivedMessageType>): Promise<void> => {
                console.log("Received message from App Device:", JSON.stringify(message, undefined, "\t"));

                try {

                    // Find the handler for this message type, execute the handler and get the response
                    if (!this.messageHandlers.has(message.type)) {
                        // noinspection ExceptionCaughtLocallyJS
                        throw new AppDeviceError(message.messageId, AppDeviceErrorType.HANDLER_FAILURE, undefined, `No handler registered for message type: ${message.type}`);
                    }
                    const response: any = await this.messageHandlers.get(message.type)(message.type, JSON.parse(message.data));

                    // Send the response back to the App Device
                    try {
                        const appDeviceResponse: AppDeviceResponse = new AppDeviceResponse(message.messageId, response instanceof EntityData ? response.toJSONString() : JSON.stringify(response));
                        console.log("Sending response to App Device:", JSON.stringify(appDeviceResponse, undefined, "\t"));
                        this.appDeviceBridge().receiveResponse(JSON.stringify(appDeviceResponse));
                    } catch (sendError) {
                        this.logError("Error sending response to App Device:", sendError);
                    }
                } catch (error) {

                    // Send the error back to the App Device
                    try {
                        const appDeviceError: AppDeviceError = error instanceof AppDeviceError ? error
                            : new AppDeviceError(message.messageId, error.errorType || AppDeviceErrorType.HANDLER_FAILURE, error.errorCode, error.message || "Error processing message");
                        console.log("Sending error to App Device:", JSON.stringify(appDeviceError, undefined, "\t"));
                        this.appDeviceBridge().receiveError(JSON.stringify(appDeviceError));
                    } catch (sendError) {
                        this.logError("Error sending error to App Device:", sendError);
                    }
                }
            },

            /**
             * Receives a response from the App Device
             * @param response The response from the app device
             */
            receiveResponse: (response: AppDeviceResponse): void => {
                console.log("Received response from App Device:", JSON.stringify(response, undefined, "\t"));

                if (!this.pendingResponses.has(response.messageId)) {
                    console.warn(`No pending response for message ID: ${response.messageId}`);
                    return;
                }

                // Complete the pending response subject with the response data
                const responseSubject: Subject<string> = this.pendingResponses.get(response.messageId);
                responseSubject.next(response.data);
                responseSubject.complete();
                this.pendingResponses.delete(response.messageId);
            },

            /**
             * Receives an error from the App Device
             * @param error The error from the app device
             */
            receiveError: (error: AppDeviceError): void => {
                this.logError("Received error from App Device:", error);

                if (!this.pendingResponses.has(error.messageId)) {
                    console.warn(`No pending response for message ID: ${error.messageId}`);
                    return;
                }

                // Complete the pending response subject with the error
                this.pendingResponses.get(error.messageId).error(error);
                this.pendingResponses.delete(error.messageId);
            }
        };
        console.log(`${AppDeviceService.APP_TO_WEB_BRIDGE_NAME} initialised`);

        // Perform a throttled app device refresh request
        onEach(this.appDeviceRefreshRequest$.pipe(
            throttleTime(AppDeviceService.APP_DEVICE_REFRESH_TIMEOUT_MS)
        ), async (request: AppDeviceRegistrationRequestData): Promise<void> => {
            console.log("Refreshing app device");
            try {
                if (!this.deviceRegistrationSent) {
                    request.secret = (await this.configurationModel.getParamConfig()).deviceSecret;
                    await this.appDeviceRegistrationService.performAction(request);
                    this.deviceRegistrationSent = true;
                    console.log(`device ${request.deviceId} has been registered`);
                }
                this.appDeviceRefreshResponse$.next(await this.sendMessage(SentMessageType.APP_DEVICE_UPDATE, await this.appDeviceEntityModel.loadEntity(request.deviceId)));
                console.log("App device refreshed");
            } catch (error) {
                console.error("Failed to refresh app device", error);
                this.appDeviceEntityModel.newEntity();
                this.appDeviceRefreshResponse$.error(error);

                // Future requests should receive the error response - we want them to try refreshing again
                this.appDeviceRefreshResponse$ = new ReplaySubject(1, AppDeviceService.APP_DEVICE_REFRESH_TIMEOUT_MS);
            }
        });

        void this.sendHandshakeMessage();
    }

    public inject(): void {
        this.notificationService = inject(NotificationService);
        this.paymentModel = inject(PaymentModel);

        onEach(this.paymentModel.paymentDetailsApplied$, () => {
            try {
                void this.refreshAppDevice("payment details applied");
            } catch (e) {
                this.logError("Error refreshing app device details", e);
            }
        }, this.destroyRef);
    }

    /**
     * Refreshes the device with the server and returns the latest server config
     */
    public async refreshAppDevice(reason: string): Promise<any> {
        console.log("App device refresh requested:", reason);

        if (!this.isAvailable()) {
            return;
        }

        this.deviceId.set((await this.configurationModel.getParamConfig()).deviceId);
        if (!this.deviceId()) {
            throw "deviceId is not configured";
        }
        this.deviceType.set((await this.configurationModel.getParamConfig()).deviceType);
        if (!this.deviceType()) {
            throw "deviceType is not configured";
        }
        const request: AppDeviceRegistrationRequestData = EntityData.create(AppDeviceRegistrationRequestData);
        request.deviceId = this.deviceId();
        request.deviceType = this.deviceType();

        this.appDeviceRefreshRequest$.next(request);
        const result: any = await firstValueFrom(this.appDeviceRefreshResponse$);
        console.log("App device refresh completed:", reason);
        return result;
    }

    /**`
     * Retrieves a string representation of the status
     */
    public get appDeviceStatusSummary(): string {
        const status: AppDeviceStatus = this.appDeviceStatus();
        let summary: string = "";
        if (status) {
            summary = summary + "Payments Ready = " + status.paymentsReady;
            summary = summary + "   |   WOA Version = " + environment.version;
            if (status.identifiers) {
                status.identifiers.forEach((value: AppDeviceStatusValue) => summary = summary + "   |   " + value.name + " = " + value.value);
            }

            if (status.statuses) {
                status.statuses.forEach((value: AppDeviceStatusValue) => summary = summary + "   |   " + value.name + " = " + value.value);
            }
        }
        return summary;
    }

    /**
     * Registers a handler for messages of a specific type
     * @param type The message type
     * @param handler The handler function
     */
    public registerMessageHandler(type: ReceivedMessageType, handler: AppDeviceMessageHandler): void {
        this.messageHandlers.set(type, handler);
        console.log(`Registered handler for message type: ${type}`);
    }

    /**
     * Sends a message to the App Device
     * @param type The message type
     * @param data The message data
     * @returns A Promise that resolves to the response from the app device
     */
    public async sendMessage(type: SentMessageType, data: any): Promise<any> {
        if (!this.isAvailable()) {
            throw "App Device not available";
        }

        // Generate a unique message ID
        const messageId: string = `msg_${Date.now()}_${this.messageCounter++}`;

        // Create a subject to handle the response
        const responseSubject: Subject<string> = new Subject<string>();
        this.pendingResponses.set(messageId, responseSubject);

        try {
            // Send the message to the app device via the web to app bridge
            const message: AppDeviceMessage<SentMessageType> = new AppDeviceMessage(messageId, type, data instanceof EntityData ? data.toJSONString() : JSON.stringify(data));
            console.log("Sending message to App Device:", JSON.stringify(message, undefined, "\t"));
            this.appDeviceBridge().receiveMessage(JSON.stringify(message));

            // Get the response
            return JSON.parse(await firstValueFrom(responseSubject));
        } finally {
            this.pendingResponses.delete(messageId);
        }
    }

    /**
     * Exits the WebView in the App Device and returns to the onboarding flow
     */
    public async exitToAppDevice(): Promise<any> {
        try {
            return this.sendMessage(SentMessageType.EXIT_VENUE, {});
        } catch (e) {
            console.error("Failed to exit to the app device", e);
        }
    }

    /**
     * Reload the WebView for an App Device, and reinitialise the terminal, etc.
     */
    public async reloadAppDevice(): Promise<any> {
        try {
            return this.sendMessage(SentMessageType.RELOAD, {});
        } catch (e) {
            console.error("Failed to reload the app device", e);
        }
    }

    /**
     * Informs the App Device that the Web App is ready to receive messages
     */
    private async sendHandshakeMessage(): Promise<void> {
        try {
            const resp: HandshakeResponse = await this.sendMessage(SentMessageType.HANDSHAKE, new HandshakeMessage());
            console.log(`App Device handshake response: status=${resp.status}, platform=${resp.platform}, version=${resp.version}`);
            this._platformVersion.set(resp.version);

            await this.sendMessage(SentMessageType.APP_DEVICE_STATUS_REQUEST, {});
        } catch (e) {
            this.logError("Error during handshake", e);
        }
    }

    private logError(message: string, error: any): void {
        console.error(message + ":", typeof error === "string" ? error : JSON.stringify(error, undefined, "\t"));
        this.notificationService.logErrorToServer(message, undefined, error);
    }
}
